<template>
    <div class="authalt__form__wrap">
        <form class="form auth__form" @submit.prevent="">
            <div class="auth__form__inner">
                <div v-if="showFormImg" class="authalt__form__img">
                    <slot name="icon" />
                </div>

                <div class="authalt__form__text">
                    <h2 class="authalt__title">
                        {{ copy.title }}
                    </h2>
                    <p v-if="copy.subtitle" class="text--xs text--center text-gray-600">
                        {{ copy.subtitle }}
                    </p>
                </div>

                <div>
                    <div v-if="hasField('fullName')">
                        <FormInput 
                            id="fullName"
                            v-model.trim="form.fullName.value"
                            type="text" 
                            size="md"
                            required 
                            border="rounded-shadow"
                            placeholder="Jane Doe"
                            label="Full Name"
                            :is-error="form.fullName.error"
                            :err-msg="form.fullName.msg"
                            @change="checkEmail"
                        />
                    </div>

                    <div v-if="hasField('email')">
                        <FormInput 
                            id="email"
                            v-model.trim="form.email.value"
                            type="email" 
                            required 
                            border="rounded-shadow"
                            placeholder="user234@xyz.com"
                            label="Email Address"
                            size="md"
                            :is-error="form.email.error"
                            :err-msg="form.email.msg"
                            @change="checkEmail"
                        />
                    </div>

                    <div v-if="hasField('country')">
                        <FormInput 
                            id="country"
                            v-model.trim="form.country.value"
                            type="text" 
                            size="md"
                            border="rounded-shadow"
                            required 
                            placeholder="Jane Doe"
                            label="Full Name"
                            :is-error="form.fullName.error"
                            :err-msg="form.fullName.msg"
                            @change="checkEmail"
                        />
                    </div>

                    <div v-if="hasField('password')">
                        <InputPassword 
                            id="password"
                            v-model="form.password.value"
                            required 
                            border="rounded-shadow"
                            label="Password" 
                            :err-msg="form.password.msg"
                            size="md"
                            @keyup="checkPassword"
                        />

                        <div v-if="authType === 'signup' || authType === 'reset'" class="auth__form__password__checks">
                            <div v-for="item in passwordChecks" :key="item">
                                <BadgeCheck :label="item" :checked="!!passwordChecksData[`${item}`]" />
                            </div>
                        </div>
                    </div>

                    <div v-if="hasField('confirmPassword')">
                        <InputPassword 
                            id="confirmPassword"
                            v-model="form.confirmPassword.value"
                            label="Confirm New Password"
                            size="md"
                            border="rounded-shadow"
                            required 
                            :err-msg="form.confirmPassword.msg"
                            @keyup="checkPassword"
                        />
                    </div>

                    <div v-if="hasField('forgotPassword')" class="flex justify-content-end">
                        <router-link class="button--link text--medium text--xs" to="/auth/initiate-reset">
                            Forgot password?
                        </router-link>
                    </div>

                    <div v-if="hasField('termsAndService')" class="checkbox__item auth--checkbox">
                        <input 
                            id="termsAndService" 
                            v-model="form.termsAndService.value" 
                            class="checkbox__input" 
                            type="checkbox"
                            :class="{ 'form__input-error': form.termsAndService.error }"
                            required
                        >
                        <span class="checkbox--label p--0 text--xs" for="termsAndService">
                            By signing up, you agree to the 
                            <router-link class="auth__form__link text--sm button--link" to="/terms">
                                Terms and Service
                            </router-link>
                        </span>
                    </div>

                    <div class="w-100 mt--20">
                        <BaseButton 
                            :loading="loading" 
                            :disabled="btnDisabled || isPasswordInvalid || loading" 
                            full-width 
                            size="md"
                            :label="copy.btnLabel" 
                            type="submit" 
                            @submit="handleContinue"
                        />
                    </div>

                    <div v-if="hasField('googleAuth')" class="mt--20">
                        <div class="or-1thbq71 mb--20">
                            <div class="or-rj288j" />
                            <span>OR</span>
                            <div class="or-1y5oso0" />
                        </div>

                        <GoogleAuthBtn 
                            :loading="externalAuthLoading" 
                            :disabled="googleAuthBtnDisabled"
                            @authenticated="handleGoogleAuth" 
                        />
                    </div>
                
                    <div v-if="bottomText.text" class="signup--notify__wrap">
                        <div class="text--xs text-gray-600 flex">
                            {{ bottomText.text }}
                            <router-link class="ml--3 button--link text--semi--bold flex" :to="bottomText.link">
                                {{ bottomText.linkText }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { authCopy, authFormFields, authFieldsToValidate } from "../dataComponents/auth";
import GoogleAuthBtn from "./GoogleAuthBtn.vue";
import { InputPassword, BaseButton, FormInput, BadgeCheck } from "../ui/index";
import { validateEmail } from "@/utils/helpers";

export default {
    name: "AuthForm",

    components: {
        BaseButton,
        InputPassword,
        FormInput,
        GoogleAuthBtn,
        BadgeCheck,
    },

    props: {
        authType: {
            type: String,
            default: "",
            validator: (value) => {
                return ["signup", "login", "reset", "initiateReset"].includes(value);
            },
        },

        loading: {
            type: Boolean,
            default: false,
        },

        error: {
            type: Object,
            default: () => {},
        },

        showFormImg: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["form-submit"],

    data() {
        return {
            showPassword: false,
            passwordErr: false,
            isEmailInvalid: false,
            form: {
                email: {
                    label: "Email address",
                    value: "",
                    error: false,
                    msg: "",
                },
                country: {
                    label: "Country",
                    value: "",
                    error: false,
                    msg: "",
                },
                password: {
                    label: "Password",
                    value: "",
                    error: false,
                    msg: "",
                },
                fullName: {
                    label: "Full name",
                    value: "",
                    error: false,
                    msg: "",
                },
                confirmPassword: {
                    label: "Confirm new password",
                    value: "",
                    error: false,
                    msg: "",
                },
                termsAndService:  {
                    value: false,
                    error: false,
                    msg: "",
                },
            },
            authCopy,
            authFormFields,
            authFieldsToValidate,
            passwordChecks: ["8 characters", "Uppercase", "Lowercase", "Special character", "Number"],
            passwordChecksData: {},
            errorCount: 0,
            externalAuthErr: false,
            externalAuthLoading: false,
        };
    },

    computed: {
        bottomText() {
            switch (this.authType) {
            case "login":
                return {
                    text: "Don't have an account?",
                    linkText: "Sign up",
                    link: "/auth/signup",
                };
            case "signup":
                return {
                    text: "Already have an account?",
                    linkText: "Log in",
                    link: "/auth/login",
                };

            case "reset":
            case "initiateReset":
                return {
                    text: "Password reset not needed?",
                    linkText: "Log in",
                    link: "/auth/login",
                };
            default:
                return {
                    text: "",
                    linkText: "",
                    link: "",
                };
            }
        },

        googleAuthBtnDisabled() {
            if (this.authType === "signup" && !this.form.termsAndService.value) {
                return true;
            }

            return false;
        },

        copy() {
            return this.authCopy[this.authType];
        },

        btnDisabled() {
            if (this.loading || this.externalAuthLoading) {
                return true;
            }

            let count = 0;
            const fields = Object.keys(this.authFieldsToValidate[this.authType]); // login: { email: boolean; password: boolean };

            for (let i = 0; i < fields.length; i++) {
                const item = fields[i];

                if (!this.form[item].value) {
                    count++;
                }
            }

            if (count > 0) return true;

            return false;
        },

        isPasswordInvalid() {
            if (this.hasField("password") || this.hasField("confirmPassword")) {
                const passwordCheckValues = Object.values(this.passwordChecksData);
                let invalidCount = 0;

                for (let i = 0; i < passwordCheckValues.length; i++) {
                    const item = passwordCheckValues[i];

                    if (!item) {
                        invalidCount++;
                    }
                }

                if (invalidCount > 0) {
                    return true;
                }

                return false;
            }

            return false;
        },

        selectedPlan() {
            return this.$store.getters["app/selectedPlan"];
        },
    },

    mounted() {
        const input = document.getElementsByTagName("input")[0];
        
        if (input) {
            input.focus();
        }
    },

    methods: {
        validateEmail,

        resetFormFields() {
            this.form.email.value = "";
            this.form.password.value = "";
            this.form.fullName.value = "";
            this.form.confirmPassword.value = "";
            this.form.termsAndService.value = "";
        },

        checkPassword(e) {
            const val = e?.target.value || e;

            this.passwordChecksData["8 characters"] = val.length > 8;
            this.passwordChecksData.Uppercase = /[A-Z]/.test(val);
            this.passwordChecksData.Lowercase = /[a-z]/.test(val);
            this.passwordChecksData.Number = /\d/.test(val);
            this.passwordChecksData["Special character"] = /[!@#$%^&*(),.?":{}|<>]/.test(val);
        },

        checkEmail(event) {            
            this.form.email.error = false;
            this.form.email.msg = "";

            const val = event?.target?.value;

            if (!val) {
                this.form.email.error = true;
                this.form.email.msg = "Email field required";
                return;
            }

            const isValid = this.validateEmail(val);
            
            if (!isValid) {
                this.form.email.error = true;
                this.form.email.msg = "Invalid email address";
                return;
            }
        },

        validateTextInput(evt) {
            const val = evt?.target.value || evt;

            if (!val) return;

            const test = /[!@#$%^&*(),.?":{}|<>]/.test(val);

            this.form.fullName.error = test;
            this.form.fullName.error = test ? "Special characters not allowed" : "";
        },

        validateFunction(type, value) {            
            switch (type) {
            case "email":
                const isValid = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value);

                if (value.length < 5) {
                    return "Email address must have a length of more than 5 characters";
                } if (!isValid) {
                    return "Please enter a valid email address";
                } else {
                    return "";
                }

            case "password":
            case "confirmPassword":
                if (value.length < 8) {
                    return "Password length should be more than 8 characters";
                } else {
                    const isValid = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value);
                        
                    if (!isValid) {
                        return "Password should contain at least 8 characters, uppercase, lowercase, special characters and a number";
                    } 
                }
                return "";
            
            case "fullName":
                if (value.length < 4) {
                    return "Full name should be greater than 4 characters";
                } else {
                    return "";
                }

            case "termsAndService":
                if (!value) {
                    return "You need to accept terms and services condition";
                } else {
                    return "";
                }
        
            default:
                return "";
            }
        },

        validateFormFields(key, label) {
            const field = this.form[`${key}`];

            if (!field.value) {
                this.form[`${key}`].error = true;
                this.form[`${key}`].msg = `${label} is required`;

                this.errorCount++;
            } 
                        
            const invalidField = this.validateFunction(key, field.value);

            if (invalidField) {
                this.form[`${key}`].error = true;
                this.form[`${key}`].msg = invalidField;
                
                this.errorCount++;
            } else {
                this.form[`${key}`].error = false;
                this.form[`${key}`].msg = "";
            }
        },

        handleContinue() {      
            this.errorCount = 0;

            for (var key of Object.keys(this.authFieldsToValidate[this.authType])) {
                const item = this.form[key];

                this.validateFormFields(key, item.value);
            }

            if (this.errorCount > 0) {
                return;
            } else {
                const payload = { body: {} };
    
                switch (this.authType) {
                case "login":
                    payload.email = this.form.email.value;
                    payload.password = this.form.password.value;
                    break;
    
                case "signup":
                    payload.body.email = this.form.email.value;
                    payload.body.password = this.form.password.value;
                    payload.body.fullName = this.form.fullName.value;
                    payload.body.acceptTerms = this.form.termsAndService.value;
                    break;
                
                case "reset":
                    payload.password = this.form.password.value;
                    payload.confirmPassword = this.form.confirmPassword.value;
                    break;
    
                case "initiateReset":
                    payload.email = this.form.email.value;
                    break;
    
                default:
                    break;
                }
    
                this.$emit("form-submit", payload);
            }
        },

        hasField(field) {
            return !this.authFormFields[this.authType] ? false : this.authFormFields[this.authType][field]; 
        },

        async handleGoogleAuth(resp) {
            try {     
                this.externalAuthLoading = true;  

                const payload = {
                    provider: "Google",
                    token: resp?.credential,
                };

                const result = await this.$store.dispatch("auth/externalLogin", payload);

                if (!result) {
                    throw new Error("Unable to login with Google. Please try again.");
                }
                
                this.$store.dispatch("auth/setAuthStatus", true);
                this.$store.dispatch("auth/setLastChecked", Date.now());
                this.$toast.success(result.message);

                if (result.userType === "new") {
                    if (this.selectedPlan === "free") {
                        this.$store.dispatch("app/toggleNewUser", true);
                        return this.$router.push("/dashboard/getting-started");
                    } 

                    return this.$router.push("/initiate-plan");
                }

                return this.$router.push({ name: "reports", query: { type: "clients", range: "1_year" } });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.externalAuthErr = errMsg;
                this.$toast.error(errMsg);
            } finally {
                this.externalAuthLoading = false;
            }
        },
    },
};
</script>